import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import api from './api';

const GalaxyJobManager = () => {
    // --- 1. KIOSK MODE CHECK ---
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const isKiosk = searchParams.get('mode') === 'kiosk';

    // --- VIEW STATE ---
    const [tab, setTab] = useState('Issue'); 

    // --- DATA STATE ---
    const [workers, setWorkers] = useState([]);
    
    // --- ISSUE FORM STATE ---
    const [issueForm, setIssueForm] = useState({ 
        kapanName: "", workerId: "", pcs: "", carats: "", 
        date: new Date().toISOString().split('T')[0] // Default Today
    });

    // --- RECEIVE STATE (SPLIT BILLING) ---
    const [pendingJobs, setPendingJobs] = useState([]);
    const [selectedJob, setSelectedJob] = useState(null);
    const [rates, setRates] = useState([]);
    
    // Split Lines: Array of billing rows
    const [splitLines, setSplitLines] = useState([
        { pcs: "", rateId: "", manualRate: "", amount: 0 }
    ]);
    const [receiveGlobal, setReceiveGlobal] = useState({
        totalReturnWeight: "",
        date: new Date().toISOString().split('T')[0]
    });

    // --- HISTORY STATE ---
    const [historyData, setHistoryData] = useState([]);
    const [filters, setFilters] = useState({
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
        partyId: ""
    });

    // --- INITIAL LOAD ---
    useEffect(() => {
        loadWorkers();
        loadPendingJobs();
    }, []);

    useEffect(() => {
        if (tab === 'History') loadHistory();
    }, [tab]);

    // --- API CALLS ---
    const loadWorkers = async () => { try { const res = await api.get('/job-workers?dept=Galaxy'); setWorkers(res.data); } catch (err) { console.error(err); } };
    const loadPendingJobs = async () => { try { const res = await api.get('/galaxy-job/pending'); setPendingJobs(res.data); } catch (err) { console.error(err); } };
    const loadHistory = async () => {
        try {
            const query = `?startDate=${filters.startDate}&endDate=${filters.endDate}&partyId=${filters.partyId}`;
            const res = await api.get(`/galaxy-job/history${query}`);
            setHistoryData(res.data);
        } catch (err) { console.error(err); }
    };

    // --- DELETE HANDLER ---
    const handleDelete = async (id, type) => {
        if (!window.confirm("⚠️ Are you sure you want to DELETE this record? This cannot be undone.")) return;
        try {
            await api.delete(`/galaxy-job/${id}`);
            alert("Deleted Successfully.");
            if (type === 'pending') {
                loadPendingJobs();
                if (selectedJob?._id === id) setSelectedJob(null);
            } else {
                loadHistory();
            }
        } catch (err) { alert("Error deleting: " + err.message); }
    };

    // --- HANDLER: ISSUE ---
    const handleIssue = async (e) => {
        e.preventDefault();
        try {
            await api.post('/galaxy-job/issue', {
                kapanName: issueForm.kapanName,
                jobWorkerId: issueForm.workerId,
                pcs: parseInt(issueForm.pcs),
                carats: parseFloat(issueForm.carats),
                date: issueForm.date
            });
            alert("✅ Issued!");
            setIssueForm({ ...issueForm, kapanName: "", pcs: "", carats: "" });
            loadPendingJobs();
        } catch (err) { alert("Error: " + err.message); }
    };

    // --- HANDLERS: RECEIVE SETUP ---
    const handleSelectForReturn = async (job) => {
        setSelectedJob(job);
        
        // Initialize with 1 line containing full pcs
        setSplitLines([{ pcs: job.issuePcs, rateId: "", manualRate: "", amount: 0 }]);
        setReceiveGlobal({ 
            totalReturnWeight: job.issueCarats, // Default to full weight
            date: new Date().toISOString().split('T')[0] 
        });

        try {
            const res = await api.get(`/galaxy-prices/list/${job.party._id}`);
            setRates(res.data);
        } catch (err) { console.error(err); }
    };

    // --- SPLIT LINE LOGIC ---
    const updateLine = (index, field, value) => {
        const newLines = [...splitLines];
        newLines[index][field] = value;

        // Auto-Calculate Amount for this line
        const pcs = parseInt(newLines[index].pcs) || 0;
        let rate = parseFloat(newLines[index].manualRate) || 0;

        // If dropdown selected, use master rate (unless manual typed)
        if (field === 'rateId' && value) {
            const r = rates.find(rt => rt._id === value);
            if (r) {
                rate = r.ratePerPiece;
                newLines[index].manualRate = ""; // Clear manual override
            }
        }
        
        // If manual typed, clear dropdown
        if (field === 'manualRate' && value) {
            newLines[index].rateId = "";
            rate = parseFloat(value);
        }

        // Fallback: If no manual rate but rateId exists (re-calc)
        if (!rate && newLines[index].rateId) {
            const r = rates.find(rt => rt._id === newLines[index].rateId);
            if (r) rate = r.ratePerPiece;
        }

        newLines[index].amount = pcs * rate;
        setSplitLines(newLines);
    };

    const addLine = () => setSplitLines([...splitLines, { pcs: "", rateId: "", manualRate: "", amount: 0 }]);
    const removeLine = (index) => setSplitLines(splitLines.filter((_, i) => i !== index));

    // Totals
    const grandTotal = splitLines.reduce((acc, curr) => acc + curr.amount, 0);
    const totalPcsReturn = splitLines.reduce((acc, curr) => acc + (parseInt(curr.pcs)||0), 0);

    // --- HANDLER: SUBMIT RECEIVE ---
    const handleReceive = async () => {
        if (!selectedJob) return;

        // Safety Check
        if (totalPcsReturn !== selectedJob.issuePcs) {
            if(!window.confirm(`⚠️ Count Mismatch!\nIssued: ${selectedJob.issuePcs}\nReturning: ${totalPcsReturn}\n\nContinue?`)) return;
        }

        try {
            // Prepare payload
            const linesPayload = splitLines.map(l => {
                let finalRate = parseFloat(l.manualRate);
                if (!finalRate && l.rateId) {
                    const r = rates.find(rt => rt._id === l.rateId);
                    if (r) finalRate = r.ratePerPiece;
                }
                return {
                    pcs: parseInt(l.pcs),
                    rate: finalRate || 0,
                    priceMasterId: l.rateId || null
                };
            });

            await api.post('/galaxy-job/receive', {
                jobId: selectedJob._id,
                lines: linesPayload,
                returnCarats: parseFloat(receiveGlobal.totalReturnWeight),
                date: receiveGlobal.date
            });
            
            alert("✅ Received & Billed!");
            setSelectedJob(null);
            loadPendingJobs();
        } catch (err) { alert("Error: " + err.message); }
    };

    // --- REPORT TOTALS ---
    const reportTotals = useMemo(() => {
        return historyData.reduce((acc, curr) => ({
            pcs: acc.pcs + (curr.returnPcs || 0),
            amount: acc.amount + (curr.totalBillAmount || curr.billingDetails?.totalAmount || 0)
        }), { pcs: 0, amount: 0 });
    }, [historyData]);


    // ================= RENDER =================
    return (
        <div className={`container-fluid ${isKiosk ? "p-4 bg-white min-vh-100" : "container mt-4"}`}>
            
            {/* --- HEADER --- */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="d-flex align-items-center gap-3">
                    {/* BACK BUTTON (Only for Kiosk) */}
                    {isKiosk && (
                        <Link to="/labor" className="btn btn-dark btn-lg shadow">
                            ⬅️ Dashboard
                        </Link>
                    )}
                    <h3 className="fw-bold text-primary mb-0">🏭 Galaxy Department</h3>
                </div>
                
                {/* Date Display */}
                {isKiosk && <span className="badge bg-secondary fs-6 p-2">{new Date().toLocaleDateString()}</span>}
            </div>

            {/* TABS */}
            <ul className="nav nav-pills mb-4 gap-2">
                <li className="nav-item"><button className={`nav-link ${tab === 'Issue' ? 'active' : ''}`} onClick={() => setTab('Issue')}>📤 Issue</button></li>
                <li className="nav-item"><button className={`nav-link ${tab === 'Receive' ? 'active' : ''}`} onClick={() => setTab('Receive')}>📥 Receive (Bill)</button></li>
                <li className="nav-item"><button className={`nav-link ${tab === 'History' ? 'active' : ''}`} onClick={() => setTab('History')}>📜 History</button></li>
            </ul>

            {/* --- TAB 1: ISSUE --- */}
            {tab === 'Issue' && (
                <div className="card shadow-sm border-primary">
                    <div className="card-header bg-primary text-white fw-bold">Issue New Job</div>
                    <div className="card-body">
                        <form onSubmit={handleIssue} className="row g-3">
                            <div className="col-md-3">
                                <label className="form-label">Issue Date</label>
                                <input type="date" className="form-control" value={issueForm.date} onChange={e => setIssueForm({...issueForm, date: e.target.value})} required />
                            </div>
                            <div className="col-md-5">
                                <label className="form-label">Kapan Name (Manual)</label>
                                <input type="text" className="form-control fw-bold" placeholder="e.g. E2" value={issueForm.kapanName} onChange={e => setIssueForm({...issueForm, kapanName: e.target.value})} required />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label">Galaxy Worker</label>
                                <select className="form-select" value={issueForm.workerId} onChange={e => setIssueForm({...issueForm, workerId: e.target.value})} required>
                                    <option value="">-- Select --</option>
                                    {workers.map(w => <option key={w._id} value={w._id}>{w.name}</option>)}
                                </select>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">Total Pcs</label>
                                <input type="number" className="form-control" value={issueForm.pcs} onChange={e => setIssueForm({...issueForm, pcs: e.target.value})} required />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">Total Carats</label>
                                <input type="number" className="form-control" value={issueForm.carats} onChange={e => setIssueForm({...issueForm, carats: e.target.value})} required />
                            </div>
                            <div className="col-12 text-end"><button className="btn btn-success fw-bold px-4 btn-lg">🚀 Send to Galaxy</button></div>
                        </form>
                    </div>
                </div>
            )}

            {/* --- TAB 2: RECEIVE --- */}
            {tab === 'Receive' && (
                <div className="row">
                    <div className="col-md-4">
                        <h6 className="text-muted border-bottom pb-2">Pending Jobs</h6>
                        <div className="list-group" style={{maxHeight:'600px', overflowY:'auto'}}>
                            {pendingJobs.map(job => (
                                <div key={job._id} className={`list-group-item list-group-item-action d-flex align-items-center ${selectedJob?._id === job._id ? 'active' : ''}`}>
                                    <div className="flex-grow-1" onClick={() => handleSelectForReturn(job)} style={{cursor:'pointer'}}>
                                        <div className="d-flex w-100 justify-content-between">
                                            <strong>{job.kapanName}</strong>
                                            <small className={selectedJob?._id === job._id ? 'text-white' : 'text-muted'}>
                                                {new Date(job.issueDate).toLocaleDateString()}
                                            </small>
                                        </div>
                                        <small>{job.party?.name} | {job.issuePcs} Pcs</small>
                                    </div>
                                    <button className="btn btn-sm btn-outline-danger ms-2 border-0" onClick={() => handleDelete(job._id, 'pending')} title="Delete">✖</button>
                                </div>
                            ))}
                            {pendingJobs.length === 0 && <div className="p-3 text-muted small text-center border rounded bg-light">No stones at Galaxy.</div>}
                        </div>
                    </div>
                    
                    <div className="col-md-8">
                        {selectedJob ? (
                            <div className="card shadow border-success">
                                <div className="card-header bg-success text-white fw-bold">Return & Split Billing</div>
                                <div className="card-body">
                                    <div className="row g-2 mb-3">
                                        <div className="col-6">
                                            <label className="small">Receive Date</label>
                                            <input type="date" className="form-control form-control-sm" value={receiveGlobal.date} onChange={e => setReceiveGlobal({...receiveGlobal, date: e.target.value})} />
                                        </div>
                                        <div className="col-6">
                                            <label className="small">Total Weight (Cts)</label>
                                            <input type="number" className="form-control form-control-sm" value={receiveGlobal.totalReturnWeight} onChange={e => setReceiveGlobal({...receiveGlobal, totalReturnWeight: e.target.value})} />
                                        </div>
                                    </div>

                                    <h6 className="text-muted border-bottom pb-2">Billing Breakdown</h6>
                                    
                                    {/* DYNAMIC SPLIT LINES */}
                                    {splitLines.map((line, index) => (
                                        <div key={index} className="row g-2 mb-2 align-items-center bg-light p-2 rounded border mx-0">
                                            <div className="col-3">
                                                <label className="small text-muted" style={{fontSize:'0.7rem'}}>Pcs</label>
                                                <input type="number" className="form-control form-control-sm fw-bold" 
                                                    value={line.pcs} onChange={e => updateLine(index, 'pcs', e.target.value)} />
                                            </div>
                                            <div className="col-4">
                                                <label className="small text-muted" style={{fontSize:'0.7rem'}}>Service / Size</label>
                                                <select className="form-select form-select-sm" value={line.rateId} onChange={e => updateLine(index, 'rateId', e.target.value)}>
                                                    <option value="">-- Manual --</option>
                                                    {rates.map(r => <option key={r._id} value={r._id}>{r.serviceName} (₹{r.ratePerPiece})</option>)}
                                                </select>
                                            </div>
                                            <div className="col-3">
                                                <label className="small text-muted" style={{fontSize:'0.7rem'}}>Or Rate (₹)</label>
                                                <input type="number" className="form-control form-control-sm" placeholder="Rate" 
                                                    value={line.manualRate} onChange={e => updateLine(index, 'manualRate', e.target.value)} />
                                            </div>
                                            <div className="col-1 text-center pt-3">
                                                {index > 0 && <button className="btn btn-sm text-danger fw-bold" onClick={() => removeLine(index)}>×</button>}
                                            </div>
                                            <div className="col-12 text-end small text-success fw-bold">
                                                Subtotal: ₹{line.amount.toLocaleString()}
                                            </div>
                                        </div>
                                    ))}

                                    <button className="btn btn-sm btn-outline-primary mb-3" onClick={addLine}>+ Add Another Rate Line</button>

                                    <div className="alert alert-secondary text-center py-2">
                                        <div className="d-flex justify-content-between px-3">
                                            <small>Issued Pcs: {selectedJob.issuePcs}</small>
                                            <small className={totalPcsReturn !== selectedJob.issuePcs ? 'text-danger fw-bold' : 'text-success'}>
                                                Returned: {totalPcsReturn}
                                            </small>
                                        </div>
                                        <h3 className="mb-0 text-success fw-bold mt-2">₹ {grandTotal.toLocaleString()}</h3>
                                    </div>

                                    <button onClick={handleReceive} className="btn btn-success w-100 fw-bold btn-lg">Confirm Bill</button>
                                </div>
                            </div>
                        ) : <div className="h-100 d-flex flex-column align-items-center justify-content-center bg-light text-muted border rounded p-5">⬅️ Select Job</div>}
                    </div>
                </div>
            )}

            {/* --- TAB 3: HISTORY --- */}
            {tab === 'History' && (
                <div className="card shadow-sm">
                    <div className="card-header bg-light d-flex gap-3 align-items-center flex-wrap">
                        <strong>Filters:</strong>
                        <input type="date" className="form-control form-control-sm" style={{width:'130px'}} value={filters.startDate} onChange={e => setFilters({...filters, startDate: e.target.value})} />
                        <span className="text-muted">to</span>
                        <input type="date" className="form-control form-control-sm" style={{width:'130px'}} value={filters.endDate} onChange={e => setFilters({...filters, endDate: e.target.value})} />
                        <select className="form-select form-select-sm" style={{width:'180px'}} value={filters.partyId} onChange={e => setFilters({...filters, partyId: e.target.value})}>
                            <option value="">All Workers</option>
                            {workers.map(w => <option key={w._id} value={w._id}>{w.name}</option>)}
                        </select>
                        <button className="btn btn-sm btn-primary" onClick={loadHistory}>🔍 Search</button>
                    </div>

                    <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-hover table-striped mb-0 text-center">
                                <thead className="table-dark">
                                    <tr>
                                        <th>Ret Date</th>
                                        <th>Kapan</th>
                                        <th>Party</th>
                                        <th>Issued</th>
                                        <th>Returned</th>
                                        <th>Bill Amount</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {historyData.length === 0 ? <tr><td colSpan="7" className="py-4 text-muted">No records found.</td></tr> :
                                        historyData.map(h => (
                                            <tr key={h._id}>
                                                <td>{new Date(h.returnDate).toLocaleDateString()}</td>
                                                <td className="fw-bold text-primary">{h.kapanName}</td>
                                                <td>{h.party?.name}</td>
                                                <td className="small text-muted">{h.issuePcs} Pcs<br/>{h.issueCarats} Ct</td>
                                                <td className="fw-bold">{h.returnPcs} Pcs<br/>{h.returnCarats} Ct</td>
                                                <td className="fw-bold text-success">
                                                    ₹{(h.totalBillAmount || h.billingDetails?.totalAmount || 0).toLocaleString()}
                                                </td>
                                                <td><button className="btn btn-sm btn-outline-danger border-0" onClick={() => handleDelete(h._id, 'history')} title="Delete Record">🗑️</button></td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                                <tfoot className="table-light">
                                    <tr>
                                        <td colSpan="4" className="text-end fw-bold">TOTAL:</td>
                                        <td className="fw-bold">{reportTotals.pcs} Pcs</td>
                                        <td className="fw-bold text-success fs-5">₹{reportTotals.amount.toLocaleString()}</td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GalaxyJobManager;